import {useCallback, useMemo} from 'react';

import {callTargetedAction, getStoreByName} from 'client/framework';
import sessionStore from 'client/Session/SessionStore';

import questionTypeActions from 'components/QuestionTypes/questionType.actions';

import {SaveStatus} from 'components/QuestionTypes/QuestionType.types';
import {useDraftGuessStore} from 'components/QuestionTypes/V2/DraftGuess';

import DraftGuessSaveStatus from './DraftGuessSaveStatus';
import {hasZustand} from 'client/components/QuestionTypes/V2/useQuestionStores';
import {debounce} from 'lodash';
import {addToast} from '@albert-io/atomic';

export default function useDraftGuess(storeName: string, submitted: boolean, storeInstance?: any) {
  const isZustandStore = useMemo(() => hasZustand(storeInstance), [storeInstance]);
  const store = isZustandStore ? storeInstance : getStoreByName(storeName);

  const loadDraftGuess = useCallback(async () => {
    if (submitted) {
      return;
    }

    if (isZustandStore) {
      try {
        const params = global.location.pathname.split('/');

        let assignmentId: string | null = null;

        if (params.some((param) => param === 'assignment')) {
          assignmentId = params[params.length - 1];
        }
        const question = (store as any).getState().question;
        const draftGuess = await useDraftGuessStore
          .getState()
          .loadDraftGuess(assignmentId, question?.getId?.());

        if (draftGuess) {
          (store as any).getState().addDraftGuessInfo(draftGuess);
        }
      } catch (_err) {
        addToast({
          title: 'Error Loading Draft',
          message: 'Error loading draft answer. Please refresh the page.',
          color: 'negative'
        });
      }

      //TODO: figure out debouncing with new stores store().cancelDebouncedSaveDraftGuess()
    } else {
      callTargetedAction({
        name: questionTypeActions.LOAD_DRAFT_GUESS,
        targetStore: storeName
      });
    }
  }, [storeName, submitted, isZustandStore, store]);

  const saveDraftGuess = useCallback(() => {
    if (submitted) {
      return;
    }

    if (isZustandStore) {
      try {
        const params = global.location.pathname.split('/');

        let assignmentId: string | null = null;

        if (params.some((param) => param === 'assignment')) {
          assignmentId = params[params.length - 1];
        }
        const buildGuessContent = (store as any).getState().buildGuessContent;
        const question = (store as any).getState().question;
        useDraftGuessStore
          .getState()
          .saveDraftGuess(assignmentId, question?.getId?.(), buildGuessContent());
      } catch (_err) {
        addToast({
          message: 'Failed to save draft guess',
          color: 'negative',
          title: ' Draft Save Error'
        });
      }

      //TODO: figure out debouncing with new stores store().saveDraftGuess()
    } else {
      callTargetedAction({
        name: questionTypeActions.SAVE_DRAFT_GUESS,
        targetStore: storeName
      });
    }
  }, [storeName, submitted, isZustandStore, store]);

  const debouncedSaveDraftGuess = useMemo(() => debounce(saveDraftGuess, 300), [saveDraftGuess]);

  const cancelDebounce = useCallback(() => {
    if (submitted) {
      return;
    }

    if (isZustandStore) {
      debouncedSaveDraftGuess.cancel();
    } else {
      callTargetedAction({
        name: questionTypeActions.CANCEL_DEBOUNCED_SAVE_DRAFT_GUESS,
        targetStore: storeName
      });
    }
  }, [storeName, submitted, isZustandStore, debouncedSaveDraftGuess]);

  const startTimer = useCallback(() => {
    if (submitted) {
      return;
    }

    if (isZustandStore) {
      const question = (store as any).getState().question;
      useDraftGuessStore.getState().startTimer(question?.getId?.());
    } else {
      callTargetedAction({
        name: questionTypeActions.START_TIMER,
        targetStore: storeName
      });
    }
  }, [storeName, submitted, isZustandStore, store]);

  const warnUser = useCallback(
    (e: BeforeUnloadEvent) => {
      if (submitted || !store || !sessionStore.hasValidSession()) {
        return;
      }

      if (isZustandStore) {
        // debouncedSaveDraftGuess.pending();
        // store().hasPendingDebouncedSaveDraftGuess() ||
        if (store().saveStatus === SaveStatus.SAVE_ERROR) {
          e.preventDefault();
          e.returnValue = 'Leaving this page may result in unsaved changes';
        }
      } else {
        if (
          store.getHasPendingDebouncedSaveDraftGuess() ||
          store.getSaveStatus() === SaveStatus.SAVE_ERROR
        ) {
          e.preventDefault();
          e.returnValue = 'Leaving this page may result in unsaved changes';
        }
      }
    },
    [store, submitted, isZustandStore]
  );

  return {
    cancelDebouncedSaveDraftGuess: cancelDebounce,
    draftGuessPromise: isZustandStore ? store().draftGuessPromise : store?.getDraftGuessPromise?.(),
    DraftGuessSaveStatus,
    loadDraftGuess,
    saveDraftGuess,
    saveStatus: isZustandStore ? store.getState().saveStatus : store?.getSaveStatus?.(),
    startTimer,
    warnUser
  };
}
